/**
 * Error Boundary da aplicação (rodada 2B1) — nunca tela branca.
 *
 * Um erro de renderização em qualquer tela cai aqui: o incidente é capturado
 * pelo reporter (com `componentStack`), e a pessoa vê uma tela simples do
 * Design System com o código de diagnóstico e três saídas — tentar de novo,
 * ir para a Home, enviar feedback (que já anexa o código).
 *
 * O que NÃO aparece: stack, exceção, detalhe técnico. O código basta para
 * localizarmos o incidente no servidor; quando o servidor ainda não confirmou
 * (sem rede), o código local é o que a pessoa cita e o que sincroniza depois.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Icon } from '../ui/icons';
import { reporter } from './instalar';

interface Props {
  children: ReactNode;
  /** Para testes: captura injetada. Padrão: o reporter da aplicação. */
  capturar?: (erro: unknown, componentStack: string | undefined) => string | null;
  /** Para testes: navegação injetada. Padrão: `window.location.assign`. */
  irPara?: (destino: string) => void;
}

interface State {
  falhou: boolean;
  codigo: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { falhou: false, codigo: null };

  static getDerivedStateFromError(): Partial<State> {
    return { falhou: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    let codigo: string | null = null;
    try {
      codigo = this.props.capturar
        ? this.props.capturar(error, info.componentStack ?? undefined)
        : reporter().capturar({ source: 'error_boundary', error, componentStack: info.componentStack ?? undefined });
    } catch {
      codigo = null; // o reporter nunca derruba a tela de erro
    }
    this.setState({ codigo });
  }

  private tentarDeNovo = () => this.setState({ falhou: false, codigo: null });

  private ir = (destino: string) => {
    if (this.props.irPara) this.props.irPara(destino);
    else window.location.assign(destino);
  };

  override render(): ReactNode {
    if (!this.state.falhou) return this.props.children;
    const { codigo } = this.state;
    return (
      <section className="screen" data-testid="tela-de-erro">
        <div className="nv-error" role="alert">
          <h2 className="nv-error__title">
            <Icon name="aviso" size={20} />
            Algo não saiu como esperado.
          </h2>
          <p className="nv-error__text">
            O que já havia sido salvo continua guardado. Você pode tentar de novo ou voltar para a Home.
            {codigo && (
              <>
                {' '}
                Se quiser nos avisar, cite o código <strong data-testid="codigo-de-diagnostico">{codigo}</strong>.
              </>
            )}
          </p>
          <div className="nv-error__acoes">
            <button type="button" className="nv-btn nv-btn--primary" onClick={this.tentarDeNovo}>
              Tentar novamente
            </button>
            <button type="button" className="nv-btn nv-btn--secondary" onClick={() => this.ir('/')}>
              Ir para a Home
            </button>
            <button type="button" className="nv-btn nv-btn--tertiary" onClick={() => this.ir('/mais?feedback=1')}>
              Enviar feedback
            </button>
          </div>
        </div>
      </section>
    );
  }
}
